import React from "react";

// Function to get language name and country from locale code
const getLanguageName = (localeCode) => {
  if (!localeCode || localeCode === "Unknown") return "Unknown";

  const localeMappings = {
    // English variants
    "en-US": "English (United States)",
    "en-GB": "English (United Kingdom)",
    "en-CA": "English (Canada)",
    "en-AU": "English (Australia)",
    "en-NZ": "English (New Zealand)",
    "en-SG": "English (Singapore)",
    "en-IN": "English (India)",
    "en-ZA": "English (South Africa)",
    
    // Chinese variants
    "zh-CN": "Chinese (Simplified)",
    "zh-TW": "Chinese (Traditional)",
    "zh-HK": "Chinese (Hong Kong)",
    "zh-SG": "Chinese (Singapore)",
    
    // Spanish variants
    "es-ES": "Spanish (Spain)",
    "es-MX": "Spanish (Mexico)",
    "es-AR": "Spanish (Argentina)",
    "es-CO": "Spanish (Colombia)",
    "es-CL": "Spanish (Chile)",
    
    // French variants
    "fr-FR": "French (France)",
    "fr-CA": "French (Canada)",
    "fr-BE": "French (Belgium)",
    "fr-CH": "French (Switzerland)",
    
    // German variants
    "de-DE": "German (Germany)",
    "de-AT": "German (Austria)",
    "de-CH": "German (Switzerland)",
    
    // Portuguese variants
    "pt-BR": "Portuguese (Brazil)",
    "pt-PT": "Portuguese (Portugal)",
    
    // Italian
    "it-IT": "Italian (Italy)",
    "it-CH": "Italian (Switzerland)",
    
    // Japanese
    "ja-JP": "Japanese (Japan)",
    
    // Korean
    "ko-KR": "Korean (South Korea)",
    
    // Dutch
    "nl-NL": "Dutch (Netherlands)",
    "nl-BE": "Dutch (Belgium)",
    
    // Russian
    "ru-RU": "Russian (Russia)",
    
    // Arabic variants
    "ar-SA": "Arabic (Saudi Arabia)",
    "ar-AE": "Arabic (UAE)",
    "ar-EG": "Arabic (Egypt)",
    
    // Other languages
    "hi-IN": "Hindi (India)",
    "sv-SE": "Swedish (Sweden)",
    "no-NO": "Norwegian (Norway)",
    "da-DK": "Danish (Denmark)",
    "fi-FI": "Finnish (Finland)",
    "pl-PL": "Polish (Poland)",
    "tr-TR": "Turkish (Turkey)",
    "th-TH": "Thai (Thailand)",
    "vi-VN": "Vietnamese (Vietnam)",
    "id-ID": "Indonesian (Indonesia)",
    "ms-MY": "Malay (Malaysia)",
    "he-IL": "Hebrew (Israel)",
    "cs-CZ": "Czech (Czechia)",
    "hu-HU": "Hungarian (Hungary)",
    "ro-RO": "Romanian (Romania)",
    "uk-UA": "Ukrainian (Ukraine)",
    "el-GR": "Greek (Greece)",
    "bg-BG": "Bulgarian (Bulgaria)",
    "hr-HR": "Croatian (Croatia)",
    "sk-SK": "Slovak (Slovakia)",
    "sl-SI": "Slovenian (Slovenia)",
  };

  // Return exact match if found
  if (localeMappings[localeCode]) {
    return localeMappings[localeCode];
  }

  // Fallback: extract language and country codes
  const parts = localeCode.split("-");
  if (parts.length === 2) {
    const lang = parts[0].toUpperCase();
    const country = parts[1].toUpperCase();
    return `${lang} (${country})`;
  }

  return localeCode.toUpperCase();
};

// Function to get flag emoji from locale code
const getLanguageFlag = (localeCode) => {
  if (!localeCode || localeCode === "Unknown") return "🌐";

  // Extract country code (the part after the hyphen)
  const parts = localeCode.split("-");
  const countryCode = parts.length === 2 ? parts[1].toLowerCase() : null;

  const countryFlags = {
    us: "🇺🇸",
    gb: "🇬🇧",
    ca: "🇨🇦",
    au: "🇦🇺",
    nz: "🇳🇿",
    sg: "🇸🇬",
    in: "🇮🇳",
    za: "🇿🇦",
    cn: "🇨🇳",
    tw: "🇹🇼",
    hk: "🇭🇰",
    es: "🇪🇸",
    mx: "🇲🇽",
    ar: "🇦🇷",
    co: "🇨🇴",
    cl: "🇨🇱",
    fr: "🇫🇷",
    be: "🇧🇪",
    ch: "🇨🇭",
    de: "🇩🇪",
    at: "🇦🇹",
    br: "🇧🇷",
    pt: "🇵🇹",
    it: "🇮🇹",
    jp: "🇯🇵",
    kr: "🇰🇷",
    nl: "🇳🇱",
    ru: "🇷🇺",
    sa: "🇸🇦",
    ae: "🇦🇪",
    eg: "🇪🇬",
    se: "🇸🇪",
    no: "🇳🇴",
    dk: "🇩🇰",
    fi: "🇫🇮",
    pl: "🇵🇱",
    tr: "🇹🇷",
    th: "🇹🇭",
    vn: "🇻🇳",
    id: "🇮🇩",
    my: "🇲🇾",
    il: "🇮🇱",
    cz: "🇨🇿",
    hu: "🇭🇺",
    ro: "🇷🇴",
    ua: "🇺🇦",
    gr: "🇬🇷",
    bg: "🇧🇬",
    hr: "🇭🇷",
    sk: "🇸🇰",
    si: "🇸🇮",
  };

  return countryFlags[countryCode] || "🌐";
};

// Function to get color based on rank
const getRankColor = (index) => {
  const colors = [
    "#667eea", // Purple
    "#48bb78", // Green
    "#f59e0b", // Orange
    "#ec4899", // Pink
    "#06b6d4", // Cyan
    "#8b5cf6", // Violet
    "#10b981", // Emerald
    "#6366f1", // Indigo
  ];
  return colors[index] || "#94a3b8"; // Default gray for others
};

// Component to render Traffic By Language List
const TrafficByLanguageList = ({ data, interval }) => {
  // Format data: [[language_code, visit_count, unique_visitors, percentage, avg_page_load_ms], ...]
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        🌍 No data available
        <small style={{ fontSize: "11px", color: "#cbd5e0", marginTop: "8px" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  const formattedData = data.map((item, index) => ({
    rank: index + 1,
    languageCode: item[0],
    languageName: getLanguageName(item[0]),
    flag: getLanguageFlag(item[0]),
    visitCount: item[1],
    uniqueVisitors: item[2],
    percentage: item[3],
    avgPageLoadMs: item[4],
    color: getRankColor(index),
  }));

  // Find max count for bar width calculation
  const maxCount = Math.max(...formattedData.map((item) => item.visitCount));

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "50px 1fr 80px 80px 60px 80px",
          gap: "8px",
          padding: "12px 16px",
          borderBottom: "2px solid #e2e8f0",
          fontSize: "10px",
          fontWeight: "700",
          color: "#718096",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          flexShrink: 0,
        }}
      >
        <div>RANK</div>
        <div>LANGUAGE</div>
        <div style={{ textAlign: "right" }}>VISITS</div>
        <div style={{ textAlign: "right" }}>VISITORS</div>
        <div style={{ textAlign: "right" }}>%</div>
        <div style={{ textAlign: "right" }}>LOAD (MS)</div>
      </div>

      {/* Scrollable List Items */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        {formattedData.map((item) => (
          <div
            key={item.rank}
            style={{
              display: "grid",
              gridTemplateColumns: "50px 1fr 80px 80px 60px 80px",
              gap: "8px",
              padding: "10px 16px",
              borderBottom: "1px solid #f7fafc",
              alignItems: "center",
              transition: "all 0.2s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f7fafc";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {/* Rank */}
            <div
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                backgroundColor: item.color,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "700",
              }}
            >
              {item.rank}
            </div>

            {/* Language with Progress Bar */}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#2d3748",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: "16px", flexShrink: 0 }}>
                  {item.flag}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.languageName}
                </span>
                {item.languageCode !== "Unknown" && (
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#a0aec0",
                      fontWeight: "500",
                      flexShrink: 0,
                    }}
                  >
                    ({item.languageCode})
                  </span>
                )}
              </div>
              <div
                style={{
                  width: "100%",
                  height: "4px",
                  backgroundColor: "#e2e8f0",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(item.visitCount / maxCount) * 100}%`,
                    height: "100%",
                    backgroundColor: item.color,
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>

            {/* Visit Count */}
            <div
              style={{
                textAlign: "right",
                fontSize: "13px",
                fontWeight: "600",
                color: "#2d3748",
              }}
            >
              {item.visitCount.toLocaleString()}
            </div>

            {/* Unique Visitors */}
            <div
              style={{
                textAlign: "right",
                fontSize: "13px",
                fontWeight: "600",
                color: "#718096",
              }}
            >
              {item.uniqueVisitors.toLocaleString()}
            </div>

            {/* Percentage */}
            <div
              style={{
                textAlign: "right",
                fontSize: "12px",
                fontWeight: "700",
                color: item.color,
              }}
            >
              {item.percentage.toFixed(1)}%
            </div>

            {/* Avg Page Load */}
            <div
              style={{
                textAlign: "right",
                fontSize: "12px",
                fontWeight: "600",
                color:
                  item.avgPageLoadMs < 1000
                    ? "#48bb78"
                    : item.avgPageLoadMs < 3000
                    ? "#f59e0b"
                    : "#ef4444",
              }}
            >
              {item.avgPageLoadMs.toFixed(0)}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Summary */}
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#f7fafc",
          borderTop: "1px solid #e2e8f0",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
          fontSize: "11px",
          color: "#718096",
          flexShrink: 0,
        }}
      >
        <div>
          <strong style={{ color: "#2d3748" }}>Languages:</strong>{" "}
          {formattedData.length}
        </div>
        <div>
          <strong style={{ color: "#2d3748" }}>Total Visits:</strong>{" "}
          {formattedData
            .reduce((sum, item) => sum + item.visitCount, 0)
            .toLocaleString()}
        </div>
        <div>
          <strong style={{ color: "#2d3748" }}>Unique Visitors:</strong>{" "}
          {formattedData
            .reduce((sum, item) => sum + item.uniqueVisitors, 0)
            .toLocaleString()}
        </div>
        <div>
          <strong style={{ color: "#2d3748" }}>Avg Load Time:</strong>{" "}
          {(
            formattedData.reduce((sum, item) => sum + item.avgPageLoadMs, 0) /
            formattedData.length
          ).toFixed(0)}{" "}
          ms
        </div>
      </div>
    </div>
  );
};

export default TrafficByLanguageList;